import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export default function CareRequestDialog({ provider, mode, searchId, careTypeId, onClose, onSent }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const isConsumer = user && user.roles?.includes("consumer");

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/care-requests", { org_ids: [provider.id], search_session_id: searchId || null, care_type_id: careTypeId || null, message, contact_phone: phone || null });
      if (data.created.length) toast.success(`Care request sent to ${provider.name}`);
      else toast.info("You already have an open request with this provider");
      onSent?.(provider.id);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!provider} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="care-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{mode === "contact" ? "Contact" : "Request care from"} {provider?.name}</DialogTitle>
          <DialogDescription>Your request shares your name, email and the general care needs from your search. Do not include medical record details.</DialogDescription>
        </DialogHeader>
        {!user ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Create a free Zunvel account to contact providers.</p>
            <Button className="w-full rounded-full bg-[#003B2D]" onClick={() => nav("/register?role=consumer")} data-testid="care-request-register-btn">Create free account</Button>
          </div>
        ) : !isConsumer ? (
          <p className="text-sm text-slate-600">Care requests can be sent from an Individual / Family account.</p>
        ) : (
          <div className="space-y-4">
            {mode === "contact" && provider?.phone && <div className="rounded-xl bg-teal-50 p-3 text-sm text-teal-900">Phone: <span className="font-semibold" data-testid="provider-phone">{provider.phone}</span></div>}
            <div className="space-y-1.5"><Label>Message to provider</Label>
              <Textarea rows={4} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Briefly describe the help you're looking for and the best time to reach you." data-testid="care-request-message-input" /></div>
            <div className="space-y-1.5"><Label>Phone (optional)</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" data-testid="care-request-phone-input" /></div>
            <Button className="w-full rounded-full bg-[#003B2D] hover:bg-[#00281F]" disabled={busy} onClick={send} data-testid="care-request-submit-btn">{busy ? "Sending…" : "Send Care Request"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
